
export function debounceAsync<T extends unknown[], U>(
  func: (...args: T) => Promise<U>,
  wait: number
): (...args: T) => Promise<U> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let pendingPromise: Promise<U> | null = null;
  let resolvePending: ((value: U) => void) | null = null;

  return async (...args: T): Promise<U> => {
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // If there's a pending promise, resolve it with the new result when it comes
    return new Promise<U>((resolve, reject) => {
      timeoutId = setTimeout(async () => {
        try {
          const result = await func(...args);
          if (resolvePending) {
            resolvePending(result);
            resolvePending = null;
          }
          resolve(result);
          pendingPromise = null;
        } catch (error) {
          reject(error);
          pendingPromise = null;
          resolvePending = null;
        }
      }, wait);

      // If there's a pending promise from a previous call, chain it
      if (pendingPromise) {
        resolvePending = resolve;
      } else {
        pendingPromise = new Promise<U>(innerResolve => {
          resolvePending = innerResolve;
        });
      }
    });
  };
}